import React, { useCallback, useRef } from 'react';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
  LAB,
  labHairline,
  labInk,
  labMuted,
  labSurface,
} from './vysorLabTheme';

type Props = {
  value: string;
  loading: boolean;
  mode: 'light' | 'dark';
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  /** Larger, centered home-state composer */
  elevated?: boolean;
  autoFocus?: boolean;
};

const VysorComposer: React.FC<Props> = ({
  value,
  loading,
  mode,
  onChange,
  onSend,
  placeholder = 'Ask VYSOR anything…',
  elevated,
  autoFocus,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const surface = labSurface(mode);
  const ink = labInk(mode);
  const muted = labMuted(mode);
  const hairline = labHairline(mode);
  const canSend = !loading && !!value.trim();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (canSend) onSend();
      }
    },
    [canSend, onSend],
  );

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: LAB.column,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          px: elevated ? 2.25 : 1.85,
          py: elevated ? 1.6 : 1.25,
          borderRadius: `${LAB.radius.composer}px`,
          border: `1px solid ${hairline}`,
          bgcolor: surface,
          boxShadow: elevated
            ? mode === 'dark'
              ? '0 0 0 1px rgba(255,255,255,0.04), 0 16px 48px rgba(0,0,0,0.45)'
              : '0 0 0 1px rgba(0,0,0,0.03), 0 16px 48px rgba(0,0,0,0.08)'
            : mode === 'dark'
              ? '0 8px 24px rgba(0,0,0,0.28)'
              : '0 4px 20px rgba(0,0,0,0.05)',
          transition: 'border-color 160ms, box-shadow 160ms',
          '&:focus-within': {
            borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.16)',
          },
        }}
      >
        <TextField
          inputRef={inputRef}
          autoFocus={autoFocus}
          fullWidth
          multiline
          maxRows={elevated ? 10 : 7}
          value={value}
          disabled={loading}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: {
              fontFamily: LAB.fontBody,
              fontSize: elevated ? '1.05rem' : '0.98rem',
              lineHeight: 1.55,
              color: ink,
              py: 0.5,
              '& textarea::placeholder': { color: muted, opacity: 1 },
            },
          }}
        />
        <Tooltip title="Send">
          <span>
            <IconButton
              onClick={onSend}
              disabled={!canSend}
              aria-label="Send"
              sx={{
                width: elevated ? 36 : 32,
                height: elevated ? 36 : 32,
                mb: 0.1,
                bgcolor: canSend ? ink : 'transparent',
                color: canSend ? (mode === 'dark' ? '#0a0a0a' : '#fff') : muted,
                border: canSend ? 'none' : `1px solid ${hairline}`,
                transition: 'background-color 140ms, transform 140ms',
                '&:hover': {
                  bgcolor: canSend ? ink : mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  transform: canSend ? 'scale(1.04)' : undefined,
                },
                '&.Mui-disabled': {
                  bgcolor: 'transparent',
                  color: muted,
                  border: `1px solid ${hairline}`,
                  opacity: 0.5,
                },
              }}
            >
              <ArrowUpwardIcon sx={{ fontSize: elevated ? 18 : 17 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default VysorComposer;
